import React, { useEffect } from 'react';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, X, FolderSearch } from 'lucide-react';
import SearchFilters from './components/SearchFilters';
import TagFilters from './components/TagFilters';
import SearchResults from './components/SearchResults';
import MobileRestriction from '../../../components/MobileRestriction';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

import {
    fetchFilterData,
    performSearch,
    initializeFromUrl,
    clearAllFilters,
    setAncestorId,
    clearAncestorInfo
} from '../../../redux/actions/search.action';
import { fetchChannels } from '../../../redux/actions/channel.action';
import {
    selectFilters,
    selectAppliedFilters,
    selectIsLoading,
    selectIsFetchingNewData,
    selectIsInitialized,
    selectHasAppliedFilters,
    selectAncestorInfo
} from '../../../redux/selectors/search.selectors';

const SearchComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const accessToken = Cookies.get('access_token');

    // Selectors
    const filters = useSelector(selectFilters);
    const appliedFilters = useSelector(selectAppliedFilters);
    const isLoading = useSelector(selectIsLoading);
    const isFetchingNewData = useSelector(selectIsFetchingNewData);
    const isInitialized = useSelector(selectIsInitialized);
    const hasAppliedFilters = useSelector(selectHasAppliedFilters);
    const ancestorInfo = useSelector(selectAncestorInfo);

    // Redirect if no access token
    useEffect(() => {
        if (!accessToken) {
            navigate('/');
        }
    }, [accessToken, navigate]);

    // Fetch filter reference data on mount
    useEffect(() => {
        if (accessToken) {
            dispatch(fetchFilterData());
            dispatch(fetchChannels()); // Fetch channels for message search filter
        }
    }, [dispatch, accessToken]);

    // Initialize from URL params on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        dispatch(initializeFromUrl(params));
    }, []);

    // Update URL when applied filters change (only after search is executed)
    useEffect(() => {
        if (!isInitialized) return;

        const params = new URLSearchParams();

        if (appliedFilters.query) params.set('query', appliedFilters.query);
        if (appliedFilters.assignee) params.set('assigned_to', appliedFilters.assignee.toString());
        if (appliedFilters.author) params.set('created_by', appliedFilters.author.toString());
        if (appliedFilters.selectedTags.length > 0) params.set('tags', appliedFilters.selectedTags.join(','));
        if (!appliedFilters.tagMethodOr) params.set('tag_method', 'and');
        if (appliedFilters.bookmarkedOnly) params.set('bookmarked_only', 'true');
        if (appliedFilters.ancestorId) params.set('ancestor_id', appliedFilters.ancestorId.toString());
        if (appliedFilters.status.length > 0) params.set('status', appliedFilters.status.join(','));
        if (appliedFilters.channelIds?.length > 0) params.set('channel_ids', appliedFilters.channelIds.join(','));
        if (appliedFilters.mediaType) params.set('media_type', appliedFilters.mediaType);
        if (appliedFilters.sentBy) params.set('sent_by', appliedFilters.sentBy.toString());
        if (appliedFilters.sentTo) params.set('sent_to', appliedFilters.sentTo.toString());
        if (appliedFilters.dateFrom) params.set('date_from', appliedFilters.dateFrom);
        if (appliedFilters.dateTo) params.set('date_to', appliedFilters.dateTo);

        const newUrl = params.toString() ? `/search?${params.toString()}` : '/search';
        navigate(newUrl, { replace: true });
    }, [appliedFilters, isInitialized, navigate]);

    // Auto-search when filters change (except query - requires explicit search button click)
    // performSearch() with default 'auto' will intelligently determine which types to search
    useEffect(() => {
        if (accessToken && isInitialized) {
            dispatch(performSearch());
        }
    }, [
        dispatch,
        accessToken,
        isInitialized,
        // Note: filters.query is intentionally excluded - search is triggered by button/Enter
        filters.assignee,
        filters.author,
        filters.selectedTags,
        filters.tagMethodOr,
        filters.bookmarkedOnly,
        filters.ancestorId,
        filters.status,
        filters.channelIds,
        filters.mediaType,
        filters.sentBy,
        filters.sentTo,
        filters.dateFrom,
        filters.dateTo
    ]);

    const handleClearFilters = () => {
        dispatch(clearAllFilters());
        navigate('/search', { replace: true });
    };

    const handleClearAncestor = () => {
        dispatch(setAncestorId(null));
        dispatch(clearAncestorInfo());
    };

    // Mobile portrait mode check
    const mobilePortrait = window?.innerWidth <= 600;
    if (mobilePortrait) {
        return <MobileRestriction />;
    }

    return (
        <div className="h-[calc(100vh-96px)] flex flex-col bg-background overflow-hidden">
            {/* Loading overlay */}
            {isLoading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Loading...</span>
                    </div>
                </div>
            )}

            {/* Compact Filters Section */}
            <div className="shrink-0 border-b bg-card/50">
                <SearchFilters onClearFilters={handleClearFilters} />
                <TagFilters />

                {/* Ancestor Info Banner */}
                {ancestorInfo && (
                    <div className="flex items-center justify-between px-4 py-1.5 bg-blue-50 dark:bg-blue-950/30 border-t border-blue-100 dark:border-blue-900">
                        <div className="flex items-center gap-2 text-sm">
                            <FolderSearch className="h-4 w-4 text-blue-600" />
                            <span className="text-muted-foreground">Searching within:</span>
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                {ancestorInfo.name}
                            </Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={handleClearAncestor}
                        >
                            <X className="h-3 w-3 mr-1" />
                            Clear
                        </Button>
                    </div>
                )}
            </div>

            {/* Results Section */}
            {!hasAppliedFilters && !isFetchingNewData ? (
                <div className="flex-1 flex items-center justify-center">
                    <Card className="p-8 text-center max-w-md border-dashed">
                        <FolderSearch className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                        <h3 className="text-lg font-semibold mb-2">Start Your Search</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Use the filters above to search for folders, links, and messages.
                            Try searching by name, author, status, channel, or tags.
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline">Bookmark filter</Badge>
                            <Badge variant="outline">Search by name</Badge>
                            <Badge variant="outline">Filter by user</Badge>
                            <Badge variant="outline">Filter by tags</Badge>
                            <Badge variant="outline">Filter by channel</Badge>
                        </div>
                    </Card>
                </div>
            ) : (
                <SearchResults />
            )}
        </div>
    );
};

export default SearchComponent;
